import type { UserAdminMessageAttributes, UserAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import DetectedIssueQ from './DetectedIssueQ'
import PlatformFeedbackQ from './PlatformFeedbackQ'
import UserQ from './UserQ'

const UserAdminMessageQ = {
  ...makeQueryModelV2<UserAdminMessageAttributes>('user_admin_message')([
    'id',
    'user_id',
    'recipient_user_id',
    'type',
    'acknowledgment_text',
    'content',
    'safety_report_id',
    'detected_issue_id',
    'session_id',
    'acknowledged_at',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: UserAdminMessageQ.user_id,
    },
    PlatformFeedback: {
      query: PlatformFeedbackQ,
      foreignKey: PlatformFeedbackQ.id,
      sourceKey: UserAdminMessageQ.safety_report_id,
    },
    DetectedIssue: {
      query: DetectedIssueQ,
      foreignKey: DetectedIssueQ.id,
      sourceKey: UserAdminMessageQ.detected_issue_id,
    },
  }),

  getUser: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: UserAdminMessageQ.user_id,
    }),

  getRecipientUser: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: UserAdminMessageQ.recipient_user_id,
    }),

  isManual: (): SQLFragment<UserAdminMessageAttributes> =>
    SQL`${UserAdminMessageQ.type} = 'manual'`,

  where: (
    {
      userId,
      recipientUserId,
      safetyReportId,
      detectedIssueId,
      sessionId,
      acknowledged,
      include = [],
      paranoid = true,
      groupBy = null,
      order = null,
      limit = null,
    }: {
      userId?: number | null | SQLFragment<number>
      recipientUserId?: number | null | SQLFragment<number>
      safetyReportId?: number | null | SQLFragment<number>
      detectedIssueId?: number | null | SQLFragment<number>
      sessionId?: number | null | SQLFragment<number>
      acknowledged?: boolean | null
      include?: IncludeAssociations
      paranoid?: boolean
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<UserAdminMessageAttributes> => SQL`
        FROM ${UserAdminMessageQ}
        ${includeAssociationsToSql(UserAdminMessageQ, include)}
        WHERE
            ${userId != null ? SQL`${UserAdminMessageQ.user_id} = ${userId} AND` : SQL``}
            ${
              recipientUserId != null
                ? SQL`${UserAdminMessageQ.recipient_user_id} = ${recipientUserId} AND`
                : SQL``
            }
            ${
              safetyReportId != null
                ? SQL`${UserAdminMessageQ.safety_report_id} = ${safetyReportId} AND`
                : SQL``
            }
            ${
              detectedIssueId != null
                ? SQL`${UserAdminMessageQ.detected_issue_id} = ${detectedIssueId} AND`
                : SQL``
            }
            ${sessionId != null ? SQL`${UserAdminMessageQ.session_id} = ${sessionId} AND` : SQL``}
            ${
              acknowledged != null
                ? acknowledged
                  ? SQL`${UserAdminMessageQ.acknowledged_at} IS NOT NULL AND`
                  : SQL`${UserAdminMessageQ.acknowledged_at} IS NULL AND`
                : SQL``
            }
            ${paranoid ? SQL`${UserAdminMessageQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,
}

export default UserAdminMessageQ
